import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TicketUserInfo {
  username: string | null;
  main_photo_url: string | null;
  city: string | null;
  state_code: string | null;
  plan_type: string;
  verification_level: string | null;
  role: string | null;
  created_at: string | null;
  last_login: string | null;
  is_online: boolean | null;
  client_ip: string | null;
  user_agent: string | null;
  last_login_at: string | null;
}

export const useTicketUserInfo = (userId: string | undefined, isStaff: boolean) => {
  return useQuery({
    queryKey: ['ticket-user-info', userId],
    queryFn: async (): Promise<TicketUserInfo | null> => {
      if (!userId) return null;

      // Fetch profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('username, main_photo_url, city, state_code, plan_type, verification_level, role, created_at, last_login, is_online')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return null;
      }

      // Fetch last login audit log
      const { data: auditLog } = await supabase
        .from('audit_logs')
        .select('client_ip, user_agent, created_at')
        .eq('user_id', userId)
        .eq('event_type', 'login')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return {
        ...profile,
        client_ip: auditLog?.client_ip?.toString() || null,
        user_agent: auditLog?.user_agent || null,
        last_login_at: auditLog?.created_at || null,
      };
    },
    enabled: !!userId && isStaff,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
